/**
 * ServiceGroupMap — full-screen SVG map of a whole service group.
 *
 * Three-layer layout:
 *   Layer 1 (left):   hosting CIs (servers/VMs)
 *   Layer 2 (center): the group's services (with role + status + access points)
 *   Layer 3 (right):  external services this group depends on / is used by
 *
 * Nodes are draggable. A side panel shows a narrative describing the setup.
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { XMarkIcon, ArrowsPointingOutIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const CI_W = 210, CI_H = 58;
const CI_H_AD = 92;  // taller when the CI is an AD Domain Controller
const SVC_W = 230, SVC_H = 96;
const EXT_W = 190, EXT_H = 54;

const COL_CI  = '#7c3aed';   // purple
const COL_SVC = '#0d9488';   // teal
const COL_EXT_OUT = '#f59e0b';
const COL_EXT_IN  = '#10b981';

const STATUS_COLOR = {
  running: '#10b981', stopped: '#ef4444', maintenance: '#f59e0b',
  degraded: '#f97316', unknown: '#94a3b8',
};
const CI_ICONS = {
  rack_element: '🖥️', virt_vm: '💻', printer: '🖨️', wifi_controller: '📡',
  wifi_access_point: '📶', firewall: '🛡️', router: '🌐', workstation: '🖥️',
  device: '📦', storage_disk: '💽', isp_circuit: '🔌', service: '⚙️', ipam_ip: '🔢',
};
const SVC_ICONS = {
  web: '🌐', database: '🗄️', api: '🔌', cache: '⚡', queue: '📨',
  container_platform: '🐳', container: '🐳', active_directory: '🏢',
  file_server: '📁', monitoring: '📊', backup: '💾', dns: '🌐',
  proxy: '🔁', vpn: '🔐', mail: '📧', erp: '🏭', other: '⚙️',
};
const DEP_ICONS = {
  uses_db: '🗄️', authenticated_by: '🔐', proxied_by: '🔁', depends_on: '⬅️',
  uses_cache: '⚡', uses_queue: '📨', replicates_to: '📋', backed_up_by: '💾',
  monitored_by: '📊', resolves_via: '🌐', stores_to: '📦',
};

function curved(x1, y1, x2, y2) {
  const mx = (x1 + x2) / 2;
  return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
}
function nearestEdge(x1, y1, x2, y2, w, h) {
  const cx = x2 + w / 2, cy = y2 + h / 2;
  const dx = x1 - cx, dy = y1 - cy;
  const ax = Math.abs(dx), ay = Math.abs(dy);
  if (ax * h > ay * w) return { x: dx > 0 ? x2 + w : x2, y: cy + (dy / (ax || 1)) * (w / 2) };
  return { x: cx + (dx / (ay || 1)) * (h / 2), y: dy > 0 ? y2 + h : y2 };
}

/** Simple markdown-ish renderer for the narrative (**bold** + *italic* + paragraphs). */
function Narrative({ text }) {
  if (!text) return null;
  const paragraphs = text.split('\n\n');
  const renderInline = (str) => {
    const parts = [];
    let rest = str;
    let key = 0;
    const re = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let lastIndex = 0;
    let m;
    while ((m = re.exec(str)) !== null) {
      if (m.index > lastIndex) parts.push(str.slice(lastIndex, m.index));
      const token = m[0];
      if (token.startsWith('**')) parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
      else parts.push(<em key={key++} style={{ color: 'var(--accent)' }}>{token.slice(1, -1)}</em>);
      lastIndex = m.index + token.length;
    }
    if (lastIndex < str.length) parts.push(str.slice(lastIndex));
    return parts;
  };
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} style={{ margin: '0 0 12px', fontSize: '0.85rem', lineHeight: 1.55, color: 'var(--text-secondary)' }}>
          {renderInline(p)}
        </p>
      ))}
    </>
  );
}

export default function ServiceGroupMap({ groupId, companyId, onClose, onEditService }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [showPanel, setShowPanel] = useState(true);
  const svgRef = useRef(null);

  const computeLayout = useCallback((d) => {
    const pos = {};
    const services = d.services || [];
    const hosts = d.hosts || [];
    const deps = d.dependencies || [];

    // Column X positions (kept compact to avoid the map drifting right)
    const X_CI = 40, X_SVC = 380, X_EXT = 740;

    // Unique external ids (services outside the group, deduped)
    const externalIds = [];
    const seen = new Set();
    deps.filter(dp => !dp.internal).forEach(dp => {
      const svcInGroup = services.find(s => s.id === dp.from_service_id);
      const extId = svcInGroup ? dp.to_service_id : dp.from_service_id;
      if (seen.has(extId)) return;
      seen.add(extId);
      externalIds.push(extId);
    });

    // Column heights → center each column vertically around a shared axis
    const ciNodeH = (h) => (h.ad_info && h.ad_info.is_dc ? CI_H_AD : CI_H);
    const ciH  = hosts.reduce((acc, h) => acc + ciNodeH(h) + 30, 0);
    const svcH = services.length * (SVC_H + 44);
    const extH = externalIds.length * (EXT_H + 24);
    const maxH = Math.max(ciH, svcH, extH, 180);
    const top = (h) => 70 + Math.max(0, (maxH - h) / 2);

    let y = top(ciH);
    hosts.forEach(h => { pos[`ci-${h.key}`] = { x: X_CI, y }; y += ciNodeH(h) + 30; });

    y = top(svcH);
    services.forEach(s => { pos[`svc-${s.id}`] = { x: X_SVC, y }; y += SVC_H + 44; });

    y = top(extH);
    externalIds.forEach(id => { pos[`ext-${id}`] = { x: X_EXT, y }; y += EXT_H + 24; });

    return pos;
  }, []);

  const load = useCallback(async () => {
    if (!groupId || !companyId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await api.getServiceGroupMap(groupId, companyId);
      setData(d);
      setPositions(computeLayout(d));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [groupId, companyId, computeLayout]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleMouseDown = (id, e) => {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const pos = positions[id];
    if (!pos) return;
    setDragging(id);
    setOffset({ x: e.clientX - rect.left - pos.x, y: e.clientY - rect.top - pos.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    setPositions(prev => ({
      ...prev,
      [dragging]: { x: e.clientX - rect.left - offset.x, y: e.clientY - rect.top - offset.y },
    }));
  };
  const handleMouseUp = () => setDragging(null);

  const externalDeps = useMemo(() => {
    if (!data) return [];
    const services = data.services || [];
    return (data.dependencies || []).filter(dp => {
      if (dp.internal) return false;
      return services.find(s => s.id === dp.from_service_id) || services.find(s => s.id === dp.to_service_id);
    });
  }, [data]);

  const canvasSize = useMemo(() => {
    const all = Object.values(positions);
    if (all.length === 0) return { w: 900, h: 500 };
    // Tight bounding box around all nodes (+ padding), so the SVG can be
    // centered by the flex container instead of drifting to one side.
    const maxX = Math.max(...all.map(p => p.x + SVC_W)) + 40;
    const maxY = Math.max(...all.map(p => p.y + SVC_H)) + 40;
    return { w: Math.max(maxX, 600), h: Math.max(maxY, 400) };
  }, [positions]);

  const group = data?.group;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--bg-body, #0f172a)',
      zIndex: 3400, display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 18px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-toolbar)',
      }}>
        <span style={{ fontSize: '1.5rem' }}>{group?.icon || '🗂️'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
            {group?.name || 'Mapa del grupo'}
          </div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
            {group?.group_type_label} · {(data?.services || []).length} servicios · {(data?.hosts || []).length} CIs
          </div>
        </div>
        <button type="button" onClick={() => setShowPanel(s => !s)}
          className="dv-btn dv-btn-secondary" style={{ fontSize: '0.78rem' }}>
          {showPanel ? 'Ocultar panel' : 'Mostrar panel'}
        </button>
        <button type="button" onClick={load}
          className="dv-btn dv-btn-secondary" style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
          <ArrowPathIcon style={{ width: 14, height: 14 }} /> Recolocar
        </button>
        <button type="button" onClick={onClose}
          className="dv-btn dv-btn-secondary" style={{
            display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem',
            fontWeight: 700, padding: '6px 14px',
          }}>
          <XMarkIcon style={{ width: 16, height: 16 }} /> Cerrar mapa
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Floating close button (always visible) */}
        <button type="button" onClick={onClose}
          style={{
            position: 'absolute', top: 12, right: 12, zIndex: 10,
            width: 38, height: 38, borderRadius: '50%',
            background: 'rgba(239,68,68,0.9)', color: '#fff', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: '1.1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
          title="Cerrar mapa (Esc)">
          ✕
        </button>
        {/* Canvas */}
        <div style={{
          flex: 1, overflow: 'auto', cursor: dragging ? 'grabbing' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
          {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Cargando mapa…</div>}
          {error && <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>⚠ {error}</div>}
          {!loading && !error && data && (
            <svg ref={svgRef} width={canvasSize.w} height={canvasSize.h}
              viewBox={`0 0 ${canvasSize.w} ${canvasSize.h}`}
              style={{ display: 'block', flexShrink: 0, margin: 'auto' }}>
              <defs>
                <marker id="gm-arr-ci" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
                  <path d="M0,0 L10,4 L0,8 Z" fill={COL_CI} /></marker>
                <marker id="gm-arr-out" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
                  <path d="M0,0 L10,4 L0,8 Z" fill={COL_EXT_OUT} /></marker>
                <marker id="gm-arr-in" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
                  <path d="M0,0 L10,4 L0,8 Z" fill={COL_EXT_IN} /></marker>
                <marker id="gm-arr-int" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
                  <path d="M0,0 L10,4 L0,8 Z" fill={COL_SVC} /></marker>
                <filter id="gm-shad"><feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.2" /></filter>
              </defs>

              {/* Column labels */}
              <text x={40} y={40} fill={COL_CI} fontSize="13" fontWeight="700">🖥️ INFRAESTRUCTURA</text>
              <text x={380} y={40} fill={COL_SVC} fontSize="13" fontWeight="700">📦 SERVICIOS DEL GRUPO</text>
              {externalDeps.length > 0 && (
                <text x={740} y={40} fill={COL_EXT_OUT} fontSize="13" fontWeight="700">🌐 EXTERNOS</text>
              )}

              {/* ── CI → service links ── */}
              {(data.hosts || []).map(h => {
                const cp = positions[`ci-${h.key}`];
                if (!cp) return null;
                const chH = (h.ad_info && h.ad_info.is_dc) ? CI_H_AD : CI_H;
                return h.service_ids.map(sid => {
                  const sp = positions[`svc-${sid}`];
                  if (!sp) return null;
                  const e1 = { x: cp.x + CI_W, y: cp.y + chH / 2 };
                  const e2 = nearestEdge(e1.x, e1.y, sp.x, sp.y, SVC_W, SVC_H);
                  return <path key={`l-ci-${h.key}-${sid}`} d={curved(e1.x, e1.y, e2.x, e2.y)}
                    stroke={COL_CI} strokeWidth="2" fill="none" markerEnd="url(#gm-arr-ci)" strokeDasharray="5,4" opacity="0.7" />;
                });
              })}

              {/* ── Internal service→service deps ── */}
              {(data.dependencies || []).filter(d => d.internal).map(d => {
                const fp = positions[`svc-${d.from_service_id}`];
                const tp = positions[`svc-${d.to_service_id}`];
                if (!fp || !tp) return null;
                const e1 = { x: fp.x + SVC_W / 2, y: fp.y + SVC_H };
                const e2 = nearestEdge(e1.x, e1.y, tp.x, tp.y, SVC_W, SVC_H);
                return <path key={`l-int-${d.id}`} d={curved(e1.x, e1.y, e2.x, e2.y)}
                  stroke={COL_SVC} strokeWidth="2.5" fill="none" markerEnd="url(#gm-arr-int)" />;
              })}

              {/* ── External deps ── */}
              {externalDeps.map(d => {
                const services = data.services || [];
                const outbound = services.find(s => s.id === d.from_service_id);
                const grpId = outbound ? d.from_service_id : d.to_service_id;
                const extId = outbound ? d.to_service_id : d.from_service_id;
                const sp = positions[`svc-${grpId}`];
                const ep = positions[`ext-${extId}`];
                if (!sp || !ep) return null;
                if (outbound) {
                  const e1 = { x: sp.x + SVC_W, y: sp.y + SVC_H / 2 };
                  const e2 = { x: ep.x, y: ep.y + EXT_H / 2 };
                  return <path key={`l-ext-${d.id}`} d={curved(e1.x, e1.y, e2.x, e2.y)}
                    stroke={COL_EXT_OUT} strokeWidth="2" fill="none" markerEnd="url(#gm-arr-out)" />;
                } else {
                  const e1 = { x: ep.x, y: ep.y + EXT_H / 2 };
                  const e2 = { x: sp.x + SVC_W, y: sp.y + SVC_H / 2 };
                  return <path key={`l-ext-${d.id}`} d={curved(e1.x, e1.y, e2.x, e2.y)}
                    stroke={COL_EXT_IN} strokeWidth="2" fill="none" markerEnd="url(#gm-arr-in)" strokeDasharray="5,4" />;
                }
              })}

              {/* ── CI nodes ── */}
              {(data.hosts || []).map(h => {
                const p = positions[`ci-${h.key}`];
                if (!p) return null;
                const ad = h.ad_info && h.ad_info.is_dc ? h.ad_info : null;
                const nodeH = ad ? CI_H_AD : CI_H;
                return (
                  <g key={`ci-${h.key}`} onMouseDown={e => handleMouseDown(`ci-${h.key}`, e)} style={{ cursor: 'move' }}>
                    <rect x={p.x} y={p.y} width={CI_W} height={nodeH} rx={9}
                      fill="var(--bg-card)" stroke={ad ? '#3b82f6' : COL_CI} strokeWidth={dragging === `ci-${h.key}` ? 3 : 2} filter="url(#gm-shad)" />
                    <text x={p.x + 16} y={p.y + 26} fontSize="20" textAnchor="middle" dominantBaseline="middle">
                      {ad ? '🖳' : (CI_ICONS[h.ci_type] || '📦')}</text>
                    <text x={p.x + 38} y={p.y + 22} fill="var(--text-primary)" fontSize="13" fontWeight="600">
                      {(h.ci_name || '').substring(0, 22)}</text>
                    <text x={p.x + 38} y={p.y + 40} fill="var(--text-muted)" fontSize="10">
                      {ad ? `${ad.site || 'Sin sitio'}` : `${h.ci_type_label} · ${h.service_ids.length} svc`}</text>
                    {ad && (
                      <>
                        {/* DC + GC badges */}
                        <rect x={p.x + 12} y={p.y + 50} width={30} height={16} rx={8} fill="rgba(59,130,246,.2)" />
                        <text x={p.x + 27} y={p.y + 61} textAnchor="middle" fill="#60a5fa" fontSize="9" fontWeight="700">DC</text>
                        {ad.is_global_catalog && (
                          <>
                            <rect x={p.x + 46} y={p.y + 50} width={30} height={16} rx={8} fill="rgba(16,185,129,.2)" />
                            <text x={p.x + 61} y={p.y + 61} textAnchor="middle" fill="#34d399" fontSize="9" fontWeight="700">GC</text>
                          </>
                        )}
                        {ad.fsmo_roles && ad.fsmo_roles.length > 0 && (
                          <>
                            <rect x={p.x + (ad.is_global_catalog ? 80 : 46)} y={p.y + 50}
                              width={CI_W - (ad.is_global_catalog ? 92 : 58)} height={16} rx={8} fill="rgba(245,158,11,.18)" />
                            <text x={p.x + (ad.is_global_catalog ? 88 : 54)} y={p.y + 61} fill="#fbbf24" fontSize="8.5" fontWeight="700">
                              ⭐ {ad.fsmo_roles.join(' ').substring(0, ad.is_global_catalog ? 16 : 22)}</text>
                          </>
                        )}
                        {/* FSMO full list line */}
                        {ad.fsmo_roles && ad.fsmo_roles.length > 2 && (
                          <text x={p.x + 12} y={p.y + 82} fill="var(--text-muted)" fontSize="9">
                            FSMO: {ad.fsmo_roles.join(', ')}</text>
                        )}
                      </>
                    )}
                  </g>
                );
              })}

              {/* ── Service nodes ── */}
              {(data.services || []).map(s => {
                const p = positions[`svc-${s.id}`];
                if (!p) return null;
                const stColor = STATUS_COLOR[s.status] || STATUS_COLOR.unknown;
                return (
                  <g key={`svc-${s.id}`} style={{ cursor: 'move' }}>
                    <g onMouseDown={e => handleMouseDown(`svc-${s.id}`, e)}>
                      <rect x={p.x} y={p.y} width={SVC_W} height={SVC_H} rx={11}
                        fill="var(--bg-card)" stroke={COL_SVC} strokeWidth={dragging === `svc-${s.id}` ? 3.5 : 2.5} filter="url(#gm-shad)" />
                      {/* status dot */}
                      <circle cx={p.x + 16} cy={p.y + 18} r={6} fill={stColor} />
                      <text x={p.x + 30} y={p.y + 22} fill="var(--text-primary)" fontSize="14" fontWeight="700">
                        {SVC_ICONS[s.service_type] || '⚙️'} {(s.display_name || s.name).substring(0, 18)}</text>
                      <text x={p.x + 16} y={p.y + 42} fill="var(--text-muted)" fontSize="11">
                        {s.service_type_label}</text>
                      {s.role && (
                        <>
                          <rect x={p.x + 16} y={p.y + 52} width={Math.min(s.role.length * 7 + 16, SVC_W - 32)} height={20} rx={10}
                            fill="rgba(13,148,136,.12)" stroke="rgba(13,148,136,.3)" strokeWidth="1" />
                          <text x={p.x + 24} y={p.y + 66} fill={COL_SVC} fontSize="10" fontWeight="600">
                            {s.role.substring(0, 26)}</text>
                        </>
                      )}
                      {s.access_point_count > 0 && (
                        <text x={p.x + SVC_W - 14} y={p.y + 88} textAnchor="end" fill="#0369a1" fontSize="10" fontWeight="600">
                          🔗 {s.access_point_count} acceso{s.access_point_count !== 1 ? 's' : ''}</text>
                      )}
                    </g>
                    {/* edit button */}
                    {onEditService && (
                      <g onClick={() => onEditService(s.id)} style={{ cursor: 'pointer' }}>
                        <rect x={p.x + SVC_W - 26} y={p.y + 8} width={18} height={18} rx={4} fill="var(--bg-toolbar)" stroke="var(--border)" />
                        <text x={p.x + SVC_W - 17} y={p.y + 21} textAnchor="middle" fontSize="11">✏️</text>
                      </g>
                    )}
                  </g>
                );
              })}

              {/* ── External nodes ── */}
              {externalDeps.map(d => {
                const services = data.services || [];
                const outbound = services.find(s => s.id === d.from_service_id);
                const extId = outbound ? d.to_service_id : d.from_service_id;
                const extName = outbound ? d.to_service_name : d.from_service_name;
                const p = positions[`ext-${extId}`];
                if (!p) return null;
                const col = outbound ? COL_EXT_OUT : COL_EXT_IN;
                return (
                  <g key={`ext-${extId}`} onMouseDown={e => handleMouseDown(`ext-${extId}`, e)} style={{ cursor: 'move' }}>
                    <rect x={p.x} y={p.y} width={EXT_W} height={EXT_H} rx={8}
                      fill="var(--bg-card)" stroke={col} strokeWidth="2"
                      strokeDasharray={outbound ? 'none' : '5,3'} filter="url(#gm-shad)" />
                    <text x={p.x + 12} y={p.y + EXT_H / 2 + 1} fontSize="15" textAnchor="middle" dominantBaseline="middle">
                      {DEP_ICONS[d.dep_type] || '⚙️'}</text>
                    <text x={p.x + 32} y={p.y + 22} fill="var(--text-primary)" fontSize="12" fontWeight="600">
                      {(extName || '').substring(0, 20)}</text>
                    <text x={p.x + 32} y={p.y + 40} fill={col} fontSize="10" fontWeight="600">
                      {d.dep_type_label}{d.port ? ` :${d.port}` : ''}</text>
                  </g>
                );
              })}

              {/* Legend */}
              <text x={canvasSize.w - 20} y={canvasSize.h - 16} textAnchor="end" fill="var(--text-muted)" fontSize="11">
                <tspan fill={COL_CI}>╌╌</tspan> aloja ·{' '}
                <tspan fill={COL_SVC}>━━</tspan> conecta ·{' '}
                <tspan fill={COL_EXT_OUT}>━━</tspan> necesita ·{' '}
                <tspan fill={COL_EXT_IN}>╌╌</tspan> usado por
              </text>
            </svg>
          )}
        </div>

        {/* Narrative panel */}
        {showPanel && (
          <div style={{
            width: 360, flexShrink: 0, borderLeft: '1px solid var(--border)',
            background: 'var(--bg-card)', overflowY: 'auto', padding: '18px 20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: '1.2rem' }}>📖</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                Cómo está montado
              </span>
            </div>
            {loading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Generando explicación…</div>
            ) : (
              <Narrative text={data?.narrative} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
